import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { MapPin, Clock, Navigation } from "lucide-react";
import api, { formatApiError } from "@/lib/api";
import { formatDateTime } from "@/lib/format";
import { StatusBadge } from "@/components/StatusBadge";
import { useI18n } from "@/context/I18nContext";

export default function LiveTrack() {
  const { t } = useI18n();
  const { token } = useParams();
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);

  const load = async () => {
    try { const { data } = await api.get(`/public/live-track/${token}`); setData(data); setError(null); }
    catch (e) { setError(formatApiError(e.response?.data?.detail) || t("trk.invalidLink")); }
  };
  useEffect(() => {
    load();
    const i = setInterval(load, 60000); // refresh each minute
    return () => clearInterval(i);
    /* eslint-disable-next-line */
  }, [token]);

  return (
    <div className="min-h-screen bg-slate-50 flex flex-col items-center py-10 px-4">
      <Link to="/" className="font-display text-2xl font-bold tracking-tight text-slate-900 mb-8">
        Rimpatrio<span className="text-blue-700">24</span>
      </Link>
      <div className="w-full max-w-xl bg-white rounded-2xl border border-slate-200 shadow-sm overflow-hidden" data-testid="livetrack-page">
        {error ? (
          <div className="p-10 text-center text-slate-500" data-testid="livetrack-error">{error}</div>
        ) : !data ? (
          <div className="p-10 text-center text-slate-400">{t("common.loading")}</div>
        ) : (
          <>
            <div className="p-6 lg:p-8 border-b border-slate-100 flex items-center justify-between">
              <div>
                <div className="text-xs uppercase tracking-wider text-slate-400">{t("trk.order")}</div>
                <h1 className="font-display text-2xl font-bold text-slate-900">{data.number}</h1>
                <p className="text-sm text-slate-500 mt-1">{data.origin} → {data.destination}</p>
              </div>
              <StatusBadge status={data.status} />
            </div>
            {data.last_location && (
              <div className="px-6 lg:px-8 py-4 bg-blue-50/50 border-b border-slate-100" data-testid="livetrack-location">
                <p className="text-sm flex items-center gap-2 text-slate-700"><Navigation size={15} className="text-blue-700" /> {t("trk.lastLocation")}: {data.last_location.lat.toFixed(4)}, {data.last_location.lng.toFixed(4)}</p>
                <p className="text-xs text-slate-400 mt-0.5">{t("trk.updated")} {formatDateTime(data.last_location.at)}</p>
                <a className="text-xs text-blue-700 hover:underline" href={`https://www.openstreetmap.org/?mlat=${data.last_location.lat}&mlon=${data.last_location.lng}#map=12/${data.last_location.lat}/${data.last_location.lng}`} target="_blank" rel="noreferrer">{t("trk.openMap")}</a>
              </div>
            )}
            <div className="p-6 lg:p-8">
              <h3 className="font-display text-lg font-semibold mb-4">{t("trk.transportStatus")}</h3>
              <div className="space-y-0">
                {(data.timeline || []).slice().reverse().map((ev, i) => (
                  <div key={i} className="flex gap-4 pb-5 relative">
                    <div className="flex flex-col items-center">
                      <div className="h-3 w-3 rounded-full bg-blue-700 mt-1.5" />
                      {i < data.timeline.length - 1 && <div className="w-px flex-1 bg-slate-200 mt-1" />}
                    </div>
                    <div className="flex-1 -mt-0.5">
                      <div className="flex items-center gap-2"><StatusBadge status={ev.status} /><span className="text-xs text-slate-400 flex items-center gap-1"><Clock size={12} /> {formatDateTime(ev.timestamp)}</span></div>
                      {ev.note && <p className="text-sm text-slate-600 mt-1">{ev.note}</p>}
                      {ev.location && <p className="text-xs text-slate-400 mt-0.5 flex items-center gap-1"><MapPin size={12} /> {ev.location}</p>}
                    </div>
                  </div>
                ))}
              </div>
            </div>
            <div className="px-6 lg:px-8 py-3 bg-slate-50 text-xs text-slate-400 text-center">
              {t("trk.linkValid")} {formatDateTime(data.expires_at)}
            </div>
          </>
        )}
      </div>
    </div>
  );
}
